import PanZoomMouseGestureHandler, {PanZoomHandler} from "./PanZoomMouseGestureHandler";
import {
    CurrSelectionBoxRectFn,
    CursorStyleUpdateFn,
    CursorType,
    CustomHandler,
    ElementHitFn,
    evaluateSelect,
    ProjectChangeUpdateFn,
    SelectionBoxHitFn,
    SelectionInfo,
    ViewPortData,
    ViewPortUpdateFn
} from "./MouseGestureUtil";
import { RenderScene, WireframeLayer } from "../scene";
import {GLRenderer} from "../glrenderer";
import {MatrixUtil, Point, PointUtil, RectangleUtil} from "../geom";
import {SelectionBoxHit} from "../elements";
import {SelectionData, SelectionDataUtil, UpdateType} from "./SelectionState";

export class MoveScaleHandler extends CustomHandler {
    public constructor(viewPortData: ViewPortData,
                       selectionInfo: SelectionInfo,
                       hitTestSelectionBox?: SelectionBoxHitFn) {
        super();
        this.viewPortData = viewPortData;
        this.selectionInfo = selectionInfo;
        this.hitTestSelectionBox = hitTestSelectionBox;
        this.panZoomHandler = new PanZoomHandler(viewPortData);
    }
    public setSelectionBoxHitFn(hitTestSelectionBox?: SelectionBoxHitFn) {
        this.hitTestSelectionBox = hitTestSelectionBox;
    }
    public setEnableRotation(v: boolean) {
        this.enableRotation = v;
    }
    public setCurrScene(scene: RenderScene, selectionData?: SelectionData) {
        this.scene = scene;
        if (selectionData) {
            this.selectionInfo.setCurrSelection(selectionData);
        }
    }
    public setCurrSelection(d: SelectionData) {
        this.selectionInfo.setCurrSelection(d);
    }
    public calcSelectionHit(pt: Point): SelectionBoxHit {
        if (this.hitTestSelectionBox) {
            return this.hitTestSelectionBox(pt);
        }
        return SelectionBoxHit.hitNothing;
    }
    public shouldStartLeftMouseDrag(pt1: Point): boolean {
        if (this.scene && this.scene.elements.length > 0 && this.selectionInfo.hasSelection()) {
            let p = this.viewPortData.clientToLogical(pt1);
            let h = this.calcSelectionHit(p);
            this.hit = h;
            if (h == SelectionBoxHit.hitMiddle) {
                return true;
            } else if (h == SelectionBoxHit.hitTopRight || h == SelectionBoxHit.hitBottomLeft ||
                h == SelectionBoxHit.hitBottomRight || h == SelectionBoxHit.hitTopLeft) {
                return true;
            }
        }
        return false;
    }
    public shouldStartTouchDrag(pt1: Point): boolean {
        return this.shouldStartLeftMouseDrag(pt1);
    }
    public shouldStartPinchZoom(pt1: Point, pt2: Point): boolean {
        if (this.scene && this.scene.elements.length > 0 && this.selectionInfo.hasSelection()) {
            return true;
        }
        return false;
    }
    public handleClick(pt1: Point): boolean {
        let p = this.viewPortData.clientToLogical(pt1);
        let r = false;
        if (this.hit == SelectionBoxHit.hitMiddle && this.enableRotation) {
            this.selectionInfo.toggleRotation();
            r = true;
        }
        if (this.hit == SelectionBoxHit.hitNothing && this.selectionInfo.hasSelection()) {
            this.selectionInfo.deselectAll();
            r = true;
        }
        if (this.hit == SelectionBoxHit.hitNothing) {
            evaluateSelect(p, this.selectionInfo, this.scene, this.viewPortData, (pt: Point) => {
                return this.calcSelectionHit(pt);
            });
            r = true;
        } else {
            r = true;
        }
        return r;
    }
    public handleTouchSelect(pt1: Point): boolean {
        return this.handleClick(pt1);
    }
    public doMouseDrag(pt1: Point, pt2: Point): boolean {
        if (PointUtil.distance(pt1, pt2) > this.dragPixelsLimit && !this.dragging) {
            if (this.hit == SelectionBoxHit.hitNothing) {
                this.hit = this.calcSelectionHit(pt1);
            }
            this.dragging = true;
            if (this.scene) {
                SelectionDataUtil.beginDrag(this.selectionInfo.getCurrSelection(), this.scene, this.hit);
            }
            this.selectionInfo.beginOperation();
        }
        if (this.dragging) {
            let p1 = this.viewPortData.clientToLogical(pt1);
            let p2 = this.viewPortData.clientToLogical(pt2);
            let a = MatrixUtil.identityMatrix();
            let type = UpdateType.MOVE;
            if (this.hit == SelectionBoxHit.hitMiddle) {
                let dx = (p2.x - p1.x);
                let dy = (p2.y - p1.y);
                a = MatrixUtil.translateMatrix(dx, dy);
            } else {
                type = this.selectionInfo.isRotation() ? UpdateType.ROTATE : UpdateType.SCALE;
                a = this.selectionInfo.calcSizeMatrix(p1, p2, this.hit, true);
            }
            this.selectionInfo.updateTransform(a, this.hit, type);
        }
        return this.dragging;
    }
    public doTouchDrag(pt1: Point, pt2: Point): boolean {
        return this.doMouseDrag(pt1, pt2);
    }
    public doMouseWheel(pt: Point, deltaY: number): boolean {
        return this.panZoomHandler.doMouseWheel(pt, deltaY);
    }
    public endMouseDrag(): void {
        this.resetState();
    }
    public endTouchDrag(): void {
        this.resetState();
    }
    public doPinchZoom(ptOrg1: Point, ptOrg2: Point, ptNew1: Point, ptNew2: Point): boolean {
        this.dragging = true;
        if (this.dragging) {
            let touchZoom = PointUtil.distance(ptNew1, ptNew2);
            let touchZoomOriginal = PointUtil.distance(ptOrg1, ptOrg2);
            let orgRect = this.selectionInfo.getOrgRect();
            if (orgRect && Math.abs(touchZoomOriginal) > 0) {
                let zoomScale = touchZoom / touchZoomOriginal;
                let a = MatrixUtil.translateMatrix(-RectangleUtil.center(orgRect).x, -RectangleUtil.center(orgRect).y);
                let b = MatrixUtil.scaleMatrix(zoomScale, zoomScale);
                let c = MatrixUtil.translateMatrix(RectangleUtil.center(orgRect).x, RectangleUtil.center(orgRect).y);
                let newMatrix = MatrixUtil.multiply(c, MatrixUtil.multiply(b, a));
                this.selectionInfo.updateTransform(newMatrix, SelectionBoxHit.hitMiddle, UpdateType.SCALE);
            }
        }
        return this.dragging;
    }
    public endPinchZoom(): void {
        this.resetState();
    }
    public resetState(): void {
        if (this.dragging) {
            this.selectionInfo.endOperation();
        }
        this.dragging = false;
        this.hit = SelectionBoxHit.hitNothing;
        this.panZoomHandler.resetState();
    }
    public isDragging(): boolean {
        return this.dragging || this.panZoomHandler.isDragging();
    }
    public getLastHit(): SelectionBoxHit {
        return this.hit;
    }
    protected dragging: boolean = false;
    protected enableRotation: boolean = false;
    protected dragPixelsLimit: number = 10;
    protected selectionInfo: SelectionInfo;
    protected hit: SelectionBoxHit = SelectionBoxHit.hitNothing;
    protected hitTestSelectionBox?: SelectionBoxHitFn;

    protected viewPortData: ViewPortData;
    protected panZoomHandler: PanZoomHandler;
    protected scene?: RenderScene;
    protected wireframeLayer?: WireframeLayer;
}

/**
 * Implementation supports pan/zoom and additional edit mode for moving/scaling designs.
 **/
class EditableMouseGestureHandler extends PanZoomMouseGestureHandler {
    /**
     *
     * @param viewPortUpdate Called when viewport changes (pan/zoom)
     * @param projectHandler Called when design changes (selection changed/design moved/scaled)
     * @param editMode Flag indicates whether to initialze to edit mode or pan/zoom mode.
     * @param v Current scene
     * @param cursorUpdateFn Called when cursor should be changed.
     * @param selectionBoxHitFn Called when need to hit test SelectionBox handles.
     */
   public constructor(editMode: boolean,
                      v: RenderScene, cursorUpdateFn?: CursorStyleUpdateFn, viewPortUpdate?: ViewPortUpdateFn, projectHandler?: ProjectChangeUpdateFn,
                      selectionBoxHitFn?: SelectionBoxHitFn, elementHitFn?: ElementHitFn, currSelectionRectFn?: CurrSelectionBoxRectFn) {
       super(viewPortUpdate);
       this.selectionInfo = new SelectionInfo(elementHitFn, projectHandler, currSelectionRectFn);
       this.selectHandler = new MoveScaleHandler(this.viewPortData, this.selectionInfo, selectionBoxHitFn);
       this._editMode = editMode;
       this.cursorUpdateFn = cursorUpdateFn;
       this.mouseGestureHandler.handlers = [(this._editMode ? this.selectHandler : this.panHandler)];
       this.setCurrScene(v);
   }
   public setEnableRotation(v: boolean) {
       this.selectHandler.setEnableRotation(v);
   }
   public setProjectUpdateFn(projectHandler?: ProjectChangeUpdateFn) {
       this.selectionInfo.setProjectUpdateFn(projectHandler);
   }
   public setElementHitFn(hitTestFn?: ElementHitFn) {
       this.selectionInfo.setHitTestFn(hitTestFn);
   }
   public setCursorUpdateFn(cursorUpdateFn?: CursorStyleUpdateFn) {
       this.cursorUpdateFn = cursorUpdateFn;
   }
   public setSelectionBoxHitFn(hitTestSelectionBox?: SelectionBoxHitFn) {
      this.selectHandler.setSelectionBoxHitFn(hitTestSelectionBox);
   }
   public setCurrSelectionRectFn(currSelectionRectFn?: CurrSelectionBoxRectFn) {
       this.selectionInfo.setCurrSelectionRectFn(currSelectionRectFn);
   }
   public setSelectionData(d: SelectionData) {
      this.selectHandler.setCurrSelection(d);
   }
   public getSelectionInfo(): SelectionInfo {
       return this.selectionInfo;
   }
   public get editMode(): boolean {
       return this._editMode;
   }
   public set editMode(v: boolean) {
       if (this._editMode != v) {
           this._editMode = v;
           if (!v) {
               this.selectionInfo.deselectAll();
           }
           this.mouseGestureHandler.handlers = [(this._editMode ? this.selectHandler : this.panHandler)];
           this.mouseGestureHandler.resetState();
       }
   }
   public setCurrScene(v: RenderScene) {
       this.selectHandler.setCurrScene(v);
   }
   public registerEvents(canvas: HTMLElement, renderer: GLRenderer, enableResizeObserver=true): () => void {
       const r = super.registerEvents(canvas, renderer, enableResizeObserver);
       this.canvas = canvas;
       return r
   }
   private getLogicalPoint(mouseEvent: MouseEvent): Point {
       let m = {x: mouseEvent.offsetX, y: mouseEvent.offsetY};
       return this.viewPortData.clientToLogical(m);
   }
   public mouseDown(mouseEvent: MouseEvent): void {
       super.mouseDown(mouseEvent);
       this.updateMouseCursor(this.calcCursorType(this.getLogicalPoint(mouseEvent)));
   }
   public mouseUp(mouseEvent: MouseEvent): void {
       super.mouseUp(mouseEvent);
       this.updateMouseCursor(this.calcCursorType(this.getLogicalPoint(mouseEvent)));
   }
   public mouseMove(mouseEvent: MouseEvent): void {
       super.mouseMove(mouseEvent);
       this.updateMouseCursor(this.calcCursorType(this.getLogicalPoint(mouseEvent)));
   }
   public calcCursorType(pt?: Point): CursorType {
       if (!this._editMode) {
           if (super.isDragging()) {
               return CursorType.Grabbing;
           } else {
               return CursorType.Grab;
           }
       } else {
           if (this.selectionInfo.hasSelection()) {
               let h = this.selectHandler.getLastHit();
               if (h == SelectionBoxHit.hitNothing && pt) {
                   h = this.selectHandler.calcSelectionHit(pt);
               }
               let isRotating = this.selectionInfo.isRotation();
               if (h == SelectionBoxHit.hitBottomLeft) {
                   return isRotating ? CursorType.SWRotate : CursorType.NESWScale;
               } else if (h == SelectionBoxHit.hitTopRight) {
                   return isRotating ? CursorType.NERotate : CursorType.NESWScale;
               } else if (h == SelectionBoxHit.hitBottomRight) {
                   return isRotating ? CursorType.SERotate : CursorType.NWSEScale;
               }
               else if (h == SelectionBoxHit.hitTopLeft) {
                   return isRotating ? CursorType.NWRotate : CursorType.NWSEScale;
               } else if (h == SelectionBoxHit.hitMiddle) {
                   return CursorType.Move;
               }
           } else {
               return CursorType.Select;
           }
       }
       return CursorType.Default;
   }
   public updateMouseCursor(type: CursorType): void {
       if (this.cursorUpdateFn && this.canvas) {
           this.cursorUpdateFn(type, this.canvas);
       }
   }
   public mouseEnter(mouseEvent: MouseEvent): void {
       super.mouseEnter(mouseEvent);
       this.updateMouseCursor(this.calcCursorType());
   }
   public mouseLeave(mouseEvent: MouseEvent): void {
       super.mouseLeave(mouseEvent);
       this.updateMouseCursor(CursorType.Default);
   }
   public isDragging(): boolean {
       return super.isDragging();
   }
   public touchStart(touchEvent: TouchEvent): void {
       super.touchStart(touchEvent);
   }
   public touchEnd(touchEvent: TouchEvent): void {
       super.touchEnd(touchEvent);
   }
   public touchMove(touchEvent: TouchEvent): void {
       super.touchMove(touchEvent);
   }
   protected selectHandler: MoveScaleHandler;
   protected selectionInfo: SelectionInfo;
   protected _editMode: boolean = false;
   protected cursorUpdateFn?: CursorStyleUpdateFn;
   protected canvas?: HTMLElement;
}

export default EditableMouseGestureHandler;